import type { Account, AccountB2B } from '@mote/api'
import { ChromeIcon } from '@mote/assets/icons'
import { CWS_URL } from '@mote/helpers/constants'
import { isExtensionSupported } from '@mote/helpers/extension'
import {
  AspectRatio,
  Block,
  Button,
  H3,
  Image,
  Text
} from '@mote/ui/components'
import { x } from '@xstyled/styled-components'
import ExtensionOverviewPNG from '../assets/extension-overview.png'
import { useOnboarding } from '../context'

export interface SurveyCompleteProps {
  onContinue: () => Promise<void>
  account?: Account | null
  isExtensionInstalled: boolean
}

export function SurveyComplete(props: SurveyCompleteProps): JSX.Element {
  const extensionSupported = isExtensionSupported()
  const onboarding = useOnboarding()

  async function handleContinue() {
    await props.onContinue()
  }

  function getIsPaidInstitutional() {
    return (
      props.account && props.account.isInstitutional && props.account.isPaid
    )
  }

  function getIsVip() {
    return props.account && props.account.isVIP
  }

  function getButtonsProps() {
    return !props.isExtensionInstalled
      ? {
          primary: {
            variant: !extensionSupported ? 'secondary' : 'primary',
            text: extensionSupported
              ? 'Install the extension'
              : 'Available on Chrome',
            icon: <ChromeIcon />,
            disabled: !extensionSupported,
            onClick: () => {
              if (extensionSupported) {
                window.open(CWS_URL, '_blank')
              }
            }
          },
          secondary: {
            'data-testid': 'complete-survey-continue-button',
            variant: extensionSupported ? 'secondary' : 'primary',
            text: extensionSupported
              ? 'Continue to my account'
              : 'Complete setup',
            loadingText: 'Loading ...',
            loading: onboarding.isLoading,
            onClick: handleContinue
          }
        }
      : {
          primary: {
            'data-testid': 'complete-survey-continue-button',
            text: 'Complete setup',
            loadingText: 'Loading ...',
            loading: onboarding.isLoading,
            onClick: handleContinue
          }
        }
  }

  function getCopies() {
    if (getIsPaidInstitutional()) {
      const account = props.account as AccountB2B
      return {
        title: 'Get started with your Mote Unlimited account',
        subtitle: `Your Unlimited account has been provided by ${account.institutionName} 🚀 Click below to get started.`
      }
    }

    if (getIsVip()) {
      return {
        title: 'You’ve been gifted an Unlimited license!',
        subtitle:
          'A Mote friend has gifted you an Unlimited mote license. Click below to get started.'
      }
    }

    return {
      title: 'You’ve unlocked 30-days of Unlimited for free 🎉',
      subtitle:
        ' Start with a 30-day trial. After, upgrade for full product access, or continue on our free plan'
    }
  }

  return (
    <Block gap={40} spreadX>
      <Block gap={24} spreadX>
        <H3 text='3xl' fontWeight='bold'>
          {getCopies().title}
        </H3>
        <Text text='md' color='grey.d3'>
          {getCopies().subtitle}
        </Text>
      </Block>
      {!props.isExtensionInstalled ? (
        <x.div
          borderRadius='xl'
          px={2}
          py={4}
          border='1px solid'
          borderColor='grey.l2'
        >
          <Image src={ExtensionOverviewPNG} alt='Extension overview' />
        </x.div>
      ) : (
        <AspectRatio
          ratio={16 / 9}
          borderRadius='xl'
          bg='grey.l4'
          w='full'
          overflow='hidden'
        >
          <iframe
            src='https://www.youtube.com/embed/GlYZNklQK34'
            title='Mote extension overview'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          ></iframe>
        </AspectRatio>
      )}
      <Block gap={24} spreadX>
        <Button
          //@ts-expect-error
          size='lg'
          {...getButtonsProps().primary}
          data-testid={'complete-survey-primary-button'}
        />
        {!props.isExtensionInstalled && (
          <Button
            data-testid={'complete-survey-secondary-button'}
            //@ts-expect-error
            size='lg'
            {...getButtonsProps().secondary}
          />
        )}
      </Block>
    </Block>
  )
}
